import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { BuildingsIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

/**
 * 16.9 Reportes consolidados multi-empresa: ventas (facturas + contado), CxC, CxP e inventario valorizado.
 */
const VISTAS = [
  { k: "sales", l: "Ventas" },
  { k: "cxc", l: "CxC" },
  { k: "cxp", l: "CxP" },
  { k: "inventory", l: "Inventario valorizado" },
];

const COLS = {
  sales: [
    { k: "facturas_count", l: "Facturas", n: true },
    { k: "total_facturado", l: "Facturado", m: true, c: "text-agro-green" },
    { k: "ventas_contado", l: "Contado (form.)", m: true },
    { k: "total_ventas", l: "Total ventas", m: true, c: "text-agro-green font-medium" },
    { k: "isv_total", l: "ISV", m: true },
    { k: "saldo_pendiente", l: "Saldo pendiente", m: true, c: "text-agro-yellow" },
  ],
  cxc: [
    { k: "facturas", l: "Facturas", n: true },
    { k: "clientes", l: "Clientes", n: true },
    { k: "corriente", l: "Corriente", m: true },
    { k: "d1_30", l: "1–30", m: true },
    { k: "d31_60", l: "31–60", m: true },
    { k: "d61_90", l: "61–90", m: true },
    { k: "d90", l: "+90", m: true, c: "text-red-400" },
    { k: "saldo", l: "Saldo total", m: true, c: "text-agro-yellow font-medium" },
  ],
  cxp: [
    { k: "facturas", l: "Facturas", n: true },
    { k: "proveedores", l: "Proveedores", n: true },
    { k: "por_vencer", l: "Por vencer", m: true },
    { k: "vencido", l: "Vencido", m: true, c: "text-red-400" },
    { k: "saldo", l: "Saldo total", m: true, c: "text-agro-yellow font-medium" },
  ],
  inventory: [
    { k: "productos", l: "Productos", n: true },
    { k: "lotes", l: "Lotes", n: true },
    { k: "unidades", l: "Unidades", n: true },
    { k: "valor", l: "Valor (PEPS)", m: true, c: "text-agro-green font-medium" },
  ],
};

export default function ConsolidatedReports() {
  const [vista, setVista] = useState("sales");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consolidado, setConsolidado] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { consolidado };
      if (vista === "sales") {
        if (from) params.fecha_desde = from;
        if (to) params.fecha_hasta = to;
      }
      const { data } = await api.get(`/reports/consolidated/${vista}`, { params });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [consolidado, vista]);

  const cols = COLS[vista];
  const tot = data?.totales || {};

  return (
    <div data-testid="consolidated-reports">
      <div className="bg-card border border-border/60 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <BuildingsIcon size={16} className="text-agro-yellow" weight="duotone" />
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Reporte</span>
          </div>
          <div className="flex bg-app border border-border rounded-md p-0.5">
            {VISTAS.map((v) => (
              <button key={v.k} onClick={() => setVista(v.k)} data-testid={`consol-vista-${v.k}`}
                className={`px-3 py-1.5 text-xs rounded ${vista === v.k ? "bg-agro-yellow text-black font-medium" : "text-text-secondary"}`}>
                {v.l}
              </button>
            ))}
          </div>
          <div className="flex bg-app border border-border rounded-md p-0.5">
            <button
              onClick={() => setConsolidado(true)}
              data-testid="toggle-consolidado"
              className={`px-3 py-1.5 text-xs rounded ${consolidado ? "bg-agro-green text-black font-medium" : "text-text-secondary"}`}
            >
              Consolidado (todas)
            </button>
            <button
              onClick={() => setConsolidado(false)}
              data-testid="toggle-empresa-actual"
              className={`px-3 py-1.5 text-xs rounded ${!consolidado ? "bg-agro-green text-black font-medium" : "text-text-secondary"}`}
            >
              Solo empresa actual
            </button>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {vista === "sales" && (
              <>
                <label className="text-[10px] uppercase tracking-wider text-text-muted">Desde</label>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                       data-testid="consol-from" className="bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
                <label className="text-[10px] uppercase tracking-wider text-text-muted">Hasta</label>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                       data-testid="consol-to" className="bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
              </>
            )}
            <button onClick={load} disabled={loading} data-testid="consol-refresh"
                    className="px-3 py-1.5 text-xs bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md flex items-center gap-1 disabled:opacity-50">
              <ArrowsClockwiseIcon size={12} /> {loading ? "..." : "Aplicar"}
            </button>
          </div>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {cols.filter((c) => c.m).slice(-4).map((c) => (
              <Stat key={c.k} label={c.l} value={fmtL(tot[c.k] || 0)} color={c.c?.includes("green") ? "green" : c.c?.includes("yellow") ? "yellow" : ""} />
            ))}
          </div>

          <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-3 pl-5">Empresa</th>
                  {cols.map((c) => <th key={c.k} className="p-3 text-right">{c.l}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.por_empresa.length === 0 ? (
                  <tr><td colSpan={cols.length + 1} className="p-12 text-center text-text-muted">Sin datos</td></tr>
                ) : data.por_empresa.map((row) => (
                  <tr key={row.company_id} className="border-t border-border/40 hover:bg-white/[0.02]"
                      data-testid={`consol-row-${row.company_id}`}>
                    <td className="p-3 pl-5 text-sm font-medium">
                      {row.company_nombre}
                      {row.company_id !== "SIN-EMPRESA" && (
                        <span className="ml-2 text-[10px] font-mono text-text-muted">#{row.company_id.slice(0, 6)}</span>
                      )}
                    </td>
                    {cols.map((c) => (
                      <td key={c.k} className={`p-3 text-right text-sm ${c.c || ""}`}>
                        {c.m ? fmtL(row[c.k] || 0) : (row[c.k] ?? 0).toLocaleString("es-HN")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {data.por_empresa.length > 1 && (
                <tfoot className="border-t-2 border-border bg-sidebar/40">
                  <tr>
                    <td className="p-3 pl-5 text-xs uppercase font-semibold">Total</td>
                    {cols.map((c) => (
                      <td key={c.k} className="p-3 text-right text-sm font-medium">
                        {c.m ? fmtL(tot[c.k] || 0) : (tot[c.k] != null ? Number(tot[c.k]).toLocaleString("es-HN") : "")}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const c = color === "green" ? "text-agro-green" : color === "yellow" ? "text-agro-yellow" : "text-text-primary";
  return (
    <div className="bg-card border border-border/60 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-xl mt-1 ${c}`}>{value}</div>
    </div>
  );
}
